import { ExcelController } from "@controllers/Excel.controller";
import { Association } from "@entities";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { ContentCopy } from "@mui/icons-material";
import BlurCircularIcon from "@mui/icons-material/BlurCircular";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupsIcon from "@mui/icons-material/Groups";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Message01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { CircularProgress, Stack } from "@mui/material";
import { associationRepo, teamRepo } from "@repositories";
import { linko } from "@shared/linko";
import { isAxiosTypedLinkoError } from "@shared/exceptions-message";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { type RelationalOrJsonEntityFilter } from "@shared/helpers/sql-relations";
import { LATEST_COMMUNICATION_RECORDS_INCLUDE, ADMIN_BULK_ACTION_LIMIT } from "../../../../helpers/admin-list-query";
import { groupCreateUrl, projectAidRequestUrl } from "@shared/helpers/constants";
import { OrganizationRoles, Roles } from "@shared/Roles";
import { useQuery } from "@tanstack/react-query";
import useCreateHyperTable, { type ToolbarAction } from "hyper-table";
import React, { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useParams } from "react-router-dom";
import { type EntityFilter, remult } from "remult";
import { MoreActions } from "../../../../components/more-actions/MoreActions";
import { UserCardPopup } from "../../../../components/popups/user-card/UserCardPopup";
import StatusDialog from "../../../../components/status-dialog/StatusDialog";
import { StatusSelect } from "../../../../components/status-select/StatusSelect";
import { TeamsSelectDialog } from "../../../../components/teams-select";
import { useAlert } from "../../../../contexts/AlertProvider";
import { useOrganization } from "../../../../contexts/OrganizationProvider";
import { useUser } from "../../../../contexts/UserProvider";
import useQuestion from "../../../../dialog-components/QuestionDialog";
import { downloadExcelFile } from "../../../../helpers/excel/export";
import SelectOpportunitiesTable from "../../select-opportunities-table";
import { TableHeaderTitles } from "../../table-header/TableHeader";
import { TableToolbarTabs } from "../../table-tabs/TableToolbarTabs";
import { type Tabs } from "../UsersTables";
import { useVolunteerColumns } from "./columns";
import { getFilters } from "./filters";
import { findParentsLink } from "./helpers";
import {
  archiveUsers,
  changeVolunteersStatus,
  handleOpportunitiesSelect,
  handleTeamsSave,
  restoreUsers,
} from "./methods";
import { RelevantVolunteersDistanceColumn } from "../../opportunities-table/RelevantVolunteersDistanceColumn";
import { splitVolunteerColumnsForDistance } from "../../opportunities-table/relevant-volunteers-distance";
import {
  SendEventParticipantsWhatsappDialog,
  type WhatsappDialogRecipient,
} from "../../opprtunity-participants/SendEventParticipantsWhatsappDialog";

type VolunteersTableProps = Tabs

const VolunteersTable: React.FC<VolunteersTableProps> = (props) => {
  const {
    activeTab = "volunteers",
    setActiveTab = () => { },
    options = [],
  } = props;
  const HyperTable = useCreateHyperTable(Association);
  const { currOrganization } = useOrganization();
  const { teamId, groupId, associationId } = useParams<
    "teamId" | "groupId" | "associationId"
  >();
  const [currentUserRowData, setCurrentUserRowData] = useState<Association>();
  const [moreActionsAnchor, setMoreActionsAnchor] =
    useState<EventTarget | null>(null);
  const question = useQuestion();
  const [teamsAnchor, setTeamsAnchor] = useState<EventTarget | null>(null);
  const [statusAnchor, setStatusAnchor] = useState<EventTarget | null>(null);
  const [teamsType, setTeamsType] = useState<"filter" | "add">("add");
  const [isAddVolunteer, setIsAddVolunteer] = useState<boolean>(false);
  const [communitiesFilter, setCommunitiesFilter] = useState<string[]>([]);
  const [isSelectOpportunitiesOpen, setIsSelectOpportunitiesOpen] =
    useState(false);
  const { setAlertText, setSeverity } = useAlert();
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState<boolean>(false);
  const [calledAssociationId, setCalledAssociationId] = useState<string | null>(
    null,
  );
  const [whatsappRecipients, setWhatsappRecipients] = useState<
    WhatsappDialogRecipient[]
  >([]);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const { currentUser } = useUser();

  const { data: team, isLoading: isTeamQueryLoading } = useQuery({
    queryKey: [`team-${teamId}-users-table`],
    queryFn: () => {
      return teamRepo.findId(teamId!);
    },
    enabled: !!teamId,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (associationId) {
      getAssociation().then((association) => {
        if (association) {
          setCurrentUserRowData(association);
        }
      });
    }
  }, [associationId]);

  const getAssociation = async () => {
    if (!associationId) return null;
    return await associationRepo.findOne({
      where: { id: associationId },
      include: {
        user: { include: { skills: { include: { skill: true } } } },
        teams: { include: { team: true } },
        groups: true,
        status: true,
      },
    });
  };

  const isHistory = activeTab === "history";

  const fixedWhere = useMemo<RelationalOrJsonEntityFilter<Association>>(
    () => ({
      organizationId: currOrganization?.id,
      isDeleted: isHistory,
      // Volunteer membership is containment of VOLUNTEER (dual VOLUNTEER+REQUESTING stays here)
      roles: {
        $some: OrganizationRoles.Volunteer,
      },
      $and: [
        {
          roles:
            activeTab === "once"
              ? {
                $some: OrganizationRoles.InOpportunity,
                $none: OrganizationRoles.Volunteer,
              }
              : {
                $some: !groupId
                  ? OrganizationRoles.Volunteer
                  : OrganizationRoles.InGroup,
              },
        },
      ],
      user: {
        $is: {
          isDeleted: false,
        },
      },
      teams: teamId
        ? {
          $some: {
            $and: [
              { teamId },
              remult.isAllowed(Roles.Admins)
                ? {}
                : {
                  teamId: {
                    $in:
                      currentUser?.teams?.map((team) => team.teamId) || [],
                  },
                },
            ],
          },
        }
        : remult.isAllowed(Roles.Admins)
          ? {}
          : {
            $some: {
              teamId: {
                $in: currentUser?.teams?.map((team) => team.teamId) || [],
              },
            },
          },
      groups: groupId
        ? {
          $some: {
            groupId,
          },
        }
        : {},
    }),
    [activeTab, teamId, groupId, isHistory],
  );

  const invitationLinks = useMemo(() => {
    const baseUrl = `https://www.we-me.app/organization/${currOrganization?.id}`;

    const links: ToolbarAction[] = [
      {
        label: `קישור הצטרפות ל${currOrganization?.name || "ארגון"}`,
        onClick: () => {
          navigator.clipboard.writeText(String(baseUrl));
          setAlertText("הקישור הועתק ללוח");
          setSeverity("success");
        },
        icon: <ContentCopy sx={{ fontSize: "1.25rem" }} />,
      },
    ];

    if (!teamId) {
      links.push({
        label: "סטטוסי מתנדבים",
        icon: null,
        onClick: () => setIsStatusDialogOpen(true),
      });
    }

    if (teamId && team) {
      links.shift();
      if (!team.isMainOrganizationTeam) {
        links.push({
          label: `קישור הצטרפות לפרויקט`,
          onClick: () => {
            navigator.clipboard.writeText(`${baseUrl}/${teamId}`);
            setAlertText("הקישור הועתק ללוח");
            setSeverity("success");
          },
          icon: <ContentCopy sx={{ fontSize: "1.25rem" }} />,
        });
        links.push({
          label: "קישור לקבוצות מאורגנות",
          icon: <ContentCopy sx={{ fontSize: "1.25rem" }} />,
          onClick: () => {
            navigator.clipboard.writeText(
              groupCreateUrl(currOrganization?.id ?? "", teamId),
            );
            setAlertText("הקישור הועתק ללוח");
            setSeverity("success");
          },
        });
        links.push({
          label: "קישור למשימות הפרויקט",
          icon: <ContentCopy sx={{ fontSize: "1.25rem" }} />,
          onClick: () => {
            navigator.clipboard.writeText(
              `https://www.we-me.app/${teamId}/search`,
            );
            setAlertText("הקישור הועתק ללוח");
            setSeverity("success");
          },
        });
        links.push({
          label: "קישור למבקשי סיוע",
          icon: <ContentCopy sx={{ fontSize: "1.25rem" }} />,
          onClick: () => {
            navigator.clipboard.writeText(projectAidRequestUrl(teamId));
            setAlertText("הקישור הועתק ללוח");
            setSeverity("success");
          },
        });
      }
    }

    return links;
  }, [
    teamId,
    currOrganization?.id,
    currOrganization?.name,
    team,
  ]);

  const buttons: ActionButton<Association>[] = useMemo(() => {
    if (isHistory) {
      return [
        {
          icon: <UnarchiveIcon />,
          onClick: async ({ whereToUpdate }) => {
            await restoreUsers(
              whereToUpdate,
              question,
              HyperTable,
              setAlertText,
              setSeverity,
            );
            return { refetch: true, resetSelection: false };
          },
          title: "שחזור",
        },
      ];
    }

    const baseButtons: ActionButton<Association>[] = [
      {
        icon: <GroupsIcon />,
        onClick: ({ event }) => {
          setTeamsType("add");
          setTeamsAnchor(event.currentTarget);
        },
        title: "שיוך לפרויקטים",
      },
      {
        icon: <BlurCircularIcon />,
        onClick: ({ event }) => setStatusAnchor(event.currentTarget),
        title: "שינוי סטטוס פעילות",
      },
      {
        title: "שיוך למשימות",
        onClick: () => setIsSelectOpportunitiesOpen(true),
        icon: <CalendarMonthIcon />,
      },
      {
        title: "שליחת וואטסאפ",
        icon: (
          <HugeiconsIcon icon={Message01Icon} size={22} strokeWidth={2} />
        ),
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;
          const associations = await associationRepo.find({
            where: whereToUpdate,
            include: { user: true },
            limit: ADMIN_BULK_ACTION_LIMIT + 1,
          });
          const toProcess = associations.slice(0, ADMIN_BULK_ACTION_LIMIT);
          const seen = new Set<string>();
          const recipients: WhatsappDialogRecipient[] = [];
          for (const row of toProcess) {
            if (!row.userId || seen.has(row.userId)) continue;
            seen.add(row.userId);
            recipients.push({
              userId: row.userId,
              name: row.user?.name,
              firstName: row.user?.firstName,
              phoneNumber: row.user?.phoneNumber,
            });
          }
          if (recipients.length === 0) {
            setAlertText("לא נבחרו מתנדבים לשליחה");
            setSeverity("warning");
            return;
          }
          setWhatsappRecipients(recipients);
          if (associations.length > ADMIN_BULK_ACTION_LIMIT) {
            setAlertText(
              `יישלח עד ${ADMIN_BULK_ACTION_LIMIT} מתנדבים. חזרו על הפעולה לשאר.`,
            );
            setSeverity("warning");
          }
          return { refetch: false, resetSelection: false };
        },
      },
      {
        icon: <DeleteOutlineIcon />,
        onClick: async ({ whereToUpdate }) => {
          await archiveUsers(
            whereToUpdate,
            question,
            HyperTable,
            setAlertText,
            setSeverity,
          );
          return { refetch: true, resetSelection: false };
        },
        title: "העברה להיסטוריה",
      },
    ];

    if (remult.isAllowed(Roles.SuperAdmin) && !isMobile && !isHistory) {
      baseButtons.push({
        icon: <MoreHorizIcon />,
        onClick: ({ event }) => setMoreActionsAnchor(event.currentTarget),
        title: "עוד",
      });
    }

    if (currentUser?.isCallCenterStation) {
      return baseButtons.filter((button) => button.title !== "שליחת וואטסאפ");
    }
    return baseButtons;
  }, [isHistory, teamsAnchor, currentUser?.isCallCenterStation]);

  const headerTitles = useMemo(() => {
    const baseHead = {
      content: "מתנדבים",
      path: "/volunteers",
    };

    if (team) {
      return [
        baseHead,
        ...findParentsLink(team),
        { content: team.name, path: `/volunteers/teams/${team.id}` },
      ];
    }

    return [baseHead];
  }, [team]);

  const volunteerColumns = useVolunteerColumns(
    currOrganization,
    HyperTable,
    groupId,
    setCalledAssociationId,
  );

  const { columnsBeforeDistance, columnsAfterDistance } = useMemo(
    () => splitVolunteerColumnsForDistance(volunteerColumns),
    [volunteerColumns],
  );

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <HyperTable
      tableId={teamId ? `team-${teamId}-users-table` : "users-table"}
      title={
        <TableHeaderTitles titles={headerTitles} loading={isTeamQueryLoading} />
      }
      components={{
        addRowButton: isHistory
          ? undefined
          : {
              onClick: () => setIsAddVolunteer(true),
              content: "הוספת מתנדב/ת",
            },
      }}
      findConfig={{
        enabled: !!currOrganization?.id,
        where: Association.hyperFilter(fixedWhere),
        include: {
          user: { include: { skills: { include: { skill: true } } } },
          teams: { include: { team: true } },
          status: true,
          ...LATEST_COMMUNICATION_RECORDS_INCLUDE,
        },
      }}
      onRowClick={(_, row) => setCurrentUserRowData(row.original)}
      getRowClassName={(row) =>
        row.original.id === calledAssociationId ? "table-row--called" : undefined
      }
    >
      <HyperTable.Toolbar moreActions={invitationLinks}>
        <TableToolbarTabs
          value={activeTab}
          onChange={setActiveTab}
          options={options}
        />
        <HyperTable.SelectColumn selectAllBehavior="cross-page" />
        {getFilters(currOrganization)?.map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
          ),
        )}
      </HyperTable.Toolbar>
      {columnsBeforeDistance.map((column) => (
        <HyperTable.Column key={column.field.toString()} {...column} />
      ))}
      <RelevantVolunteersDistanceColumn />
      {columnsAfterDistance.map((column) => (
        <HyperTable.Column key={column.field.toString()} {...column} />
      ))}
      <StatusDialog
        open={isStatusDialogOpen}
        onClose={() => {
          setIsStatusDialogOpen(false);
          HyperTable.context?.current?.refetch("remove");
        }}
        type="volunteer"
      />
      <UserCardPopup
        handleSave={() => HyperTable.context.current?.refetch("remove")}
        userData={currentUserRowData}
        setUserData={setCurrentUserRowData}
        isAdd={isAddVolunteer}
        setIsAdd={setIsAddVolunteer}
      />
      <MoreActions
        anchorEl={moreActionsAnchor}
        deleteLabel="העברה להיסטוריה"
        handleChange={async () => {
          await archiveUsers(
            HyperTable.context?.current?.allSelected
              ? (HyperTable.context?.current
                  ?.finalWhere as EntityFilter<Association>)
              : associationRepo.metadata.idMetadata.getIdFilter(
                  ...HyperTable.context?.current?.selectedIds!,
                ),
            question,
            HyperTable,
            setAlertText,
            setSeverity,
          );
          setMoreActionsAnchor(null);
        }}
        onClose={() => setMoreActionsAnchor(null)}
        handleExport={async () => {
          // Download the Excel file
          const excelResult = await ExcelController.exportUsersToExcel(
            HyperTable.context?.current?.allSelected
              ? (HyperTable.context?.current
                ?.finalWhere as EntityFilter<Association>)
              : associationRepo.metadata.idMetadata.getIdFilter(
                ...HyperTable.context?.current?.selectedIds!,
              ),
          );
          downloadExcelFile(excelResult);
        }}
      />
      <TeamsSelectDialog
        open={!!teamsAnchor}
        value={teamsType === "add" ? [] : communitiesFilter}
        onClose={() => {
          setTeamsAnchor(null);
        }}
        onSave={(chosenTeams) => {
          setTeamsAnchor(null);
          handleTeamsSave(
            HyperTable.context?.current?.allSelected
              ? (HyperTable.context?.current
                ?.finalWhere as EntityFilter<Association>)
              : associationRepo.metadata.idMetadata.getIdFilter(
                ...HyperTable.context?.current?.selectedIds!,
              ),
            chosenTeams as string[],
            HyperTable,
          );
        }}
      />
      <StatusSelect
        anchorEl={statusAnchor}
        onSelect={(option) => {
          changeVolunteersStatus(
            option,
            setAlertText,
            setSeverity,
            setStatusAnchor,
            HyperTable,
          );
        }}
        onClose={() => setStatusAnchor(null)}
      />
      <SelectOpportunitiesTable
        initialSelectedOpportunities={[]}
        open={isSelectOpportunitiesOpen}
        onSave={(selected) => {
          handleOpportunitiesSelect(
            selected,
            HyperTable.context?.current?.allSelected
              ? (HyperTable.context?.current
                ?.finalWhere as EntityFilter<Association>)
              : associationRepo.metadata.idMetadata.getIdFilter(
                ...HyperTable.context?.current?.selectedIds!,
              ),
          );
          setAlertText("המשימות שויכו בהצלחה");
          setSeverity("success");
          setIsSelectOpportunitiesOpen(false);
          HyperTable.context?.current?.table.toggleAllRowsSelected(false);
        }}
        onClose={() => setIsSelectOpportunitiesOpen(false)}
      />
      <HyperTable.ActionsBar buttons={buttons} />
      <SendEventParticipantsWhatsappDialog
        open={whatsappRecipients.length > 0}
        onClose={() => {
          if (!whatsappSending) setWhatsappRecipients([]);
        }}
        sending={whatsappSending}
        recipientCount={whatsappRecipients.length}
        recipients={whatsappRecipients}
        audience="directory"
        onSend={async (templateKey, customFields, confirmedRecentUserIds) => {
          setWhatsappSending(true);
          try {
            const { data: result } =
              await linko.opportunity.mutations.sendVolunteersWhatsapp({
                userIds: whatsappRecipients.map((recipient) => recipient.userId),
                templateKey,
                customFields,
                confirmedRecentUserIds,
              });
            setAlertText(result.message);
            setSeverity(result.failed === 0 && result.skipped === 0 ? "success" : "warning");
            setWhatsappRecipients([]);
          } catch (error: unknown) {
            setAlertText(
              isAxiosTypedLinkoError(error)
                ? error.linkoError.message
                : "שליחת הוואטסאפ נכשלה",
            );
            setSeverity("error");
          } finally {
            setWhatsappSending(false);
          }
        }}
      />
    </HyperTable>
  );
};

export default VolunteersTable;
